'use strict';

import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { format } from 'date-fns';
import update from 'immutability-helper';
import cn from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { appSelectors, appOperations } from '@/state/domains/app';
import { ordersOperations, ordersSelectors } from '@/state/domains/orders';
import { jobsTableSelectors, jobsTableOperations } from '@/state/domains/jobsTable';
import { usersSelectors, usersOperations } from '@/state/domains/users';

import ColumnSelect from '@/views/common/ColumnSelect';
import Table from './Table';
import Pagination from './Pagination';
import Sort from './Sort';
import ColumnOpt from './ColumnOpt';
import Assign from './Assign';

import { useIsFirstRender } from '@/views/utils';

import s from './styles.scss';

const sx = cn.bind(s);

const TABLE_TAB = 'TABLE_TAB';
const MAP_TAB = 'MAP_TAB';
const SINGLE_SORT = 'SINGLE_SORT';
const MULTI_SORT = 'MULTI_SORT';

const sortModes = {
	SINGLE_SORT: 'Single',
	MULTI_SORT: 'Multi',
};

const acronyms = {};

const makeAcronym = v => {

	if (v in acronyms) {
		return acronyms[ v ];
	}

	acronyms[ v ] = v.split(/\s/).reduce(
		(acc, w) => acc + w.charAt(0).toUpperCase(), ''
	);

	return acronyms[ v ];
};

const getUserSettings = user => {

	return update(user.settings || {}, {
		ui: ui => update(ui || {}, {
			jobsTable: jobsTable => jobsTable || {}
		}),
	});
};

const filterOptionsMakers = {
	__default: vals => vals.map(v => ({ label: v, value: v })),
	company: companies => companies.map(c => ({
		label: makeAcronym(c.name),
		value: c.id,
	})),
};

const addFilterOptions = (distinct, filters) => Object.entries(distinct).reduce(
	(opts, [ key, _options ]) => {

		let options;

		if (key in filterOptionsMakers) {
			options = filterOptionsMakers[ key ](_options);
		} else {
			options = filterOptionsMakers.__default(_options);
		}

		return {
			...opts,
			[ key ]: options,
		};
	},
	{}
);

const tableColumns = [
	{
		key: 'company',
		label: 'Company',
		visible: true,
		sort: 0,
		getValue: (job, ctx) => {

			const company = ctx.distinct.company.find(
				c => c.id === job.companyId
			);

			return makeAcronym(company.name);
		},
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
	{
		key: 'id',
		label: 'Job ID',
		visible: true,
		sort: 0,
		filter: {
			type: 'text',
			value: '',
			buf: '',
		},
	},
	{
		key: 'type',
		label: 'Type',
		visible: true,
		sort: 0,
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
	{
		key: 'recipient',
		label: 'Recipient',
		visible: true,
		sort: 0,
		filter: {
			type: 'text',
			value: '',
			buf: '',
		},
	},
	{
		key: 'task',
		label: 'Task',
		visible: true,
		sort: 0,
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
	{
		key: 'deadline',
		label: 'Deadline',
		visible: true,
		sort: 0,
		getValue: job => {
			if (job.deadline) {
				return format(
					new Date(job.deadline),
					'EEE MMM do y'
				);
			}
			return '';
		},
		filter: {
			type: 'date',
			value: [ '', '' ],
		},
	},
	{
		key: 'completed',
		label: 'Completed',
		visible: true,
		sort: 0,
		getValue: job => job.completed ? 'Yes' : 'No',
		filter: {
			type: 'boolean',
			value: undefined,
		},
	},
	{
		key: 'address',
		label: 'Address',
		visible: true,
		sort: 0,
		filter: {
			type: 'text',
			value: '',
			buf: '',
		},
	},
	{
		key: 'neighborhood',
		label: 'Neighborhood',
		visible: false,
		sort: 0,
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
	{
		key: 'city',
		label: 'City',
		visible: true,
		sort: 0,
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
	{
		key: 'state',
		label: 'State',
		visible: true,
		sort: 0,
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
	{
		key: 'zip',
		label: 'Zip',
		visible: true,
		sort: 0,
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
	{
		key: 'county',
		label: 'County',
		visible: true,
		sort: 0,
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
	{
		key: 'mornings',
		label: 'Mornings',
		shortLabel: 'M',
		visible: true,
		sort: 0,
		filter: {
			type: 'boolean',
			value: undefined,
		},
	},
	{
		key: 'days',
		label: 'Days',
		shortLabel: 'D',
		visible: true,
		sort: 0,
		filter: {
			type: 'boolean',
			value: undefined,
		},
	},
	{
		key: 'evenings',
		label: 'Evenings',
		shortLabel: 'E',
		visible: true,
		sort: 0,
		filter: {
			type: 'boolean',
			value: undefined,
		},
	},
	{
		key: 'weekends',
		label: 'Weekends',
		shortLabel: 'W',
		visible: true,
		sort: 0,
		filter: {
			type: 'boolean',
			value: undefined,
		},
	},
	{
		key: 'all',
		label: 'All times of day',
		shortLabel: 'A',
		visible: true,
		sort: 0,
		getValue: job => job.all ? 'Yes' : 'No',
		filter: {
			type: 'boolean',
			value: undefined,
		},
	},
	{
		key: 'invalid',
		label: 'Invalid',
		visible: true,
		sort: 0,
		getValue: job => job.invalid ? 'Yes' : 'No',
		filter: {
			type: 'boolean',
			value: undefined,
		},
	},
	{
		key: 'dateReceived',
		label: 'Date Received',
		visible: true,
		sort: 0,
		getValue: job => {
			if (job.dateReceived) {
				return format(
					new Date(job.dateReceived),
					'EEE MMM do y'
				);
			}
			return '';
		},
		filter: {
			type: 'date',
			value: [ '', '' ],
		},
	},
	{
		key: 'status',
		label: 'Status',
		visible: true,
		sort: 0,
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
	{
		key: 'statusDue',
		label: 'Status Due',
		visible: true,
		sort: 0,
		getValue: job => {
			if (job.statusDue) {
				return format(
					new Date(job.statusDue),
					'EEE MMM do y'
				);
			}
			return '';
		},
		filter: {
			type: 'date',
			value: [ '', '' ],
		},
	},
	{
		key: 'user',
		label: 'Assigned To',
		visible: true,
		sort: 0,
		getValue: job => {
			return job.assignedTo ? job.assignedTo.username : '';
		},
		filter: {
			type: 'list',
			multi: true,
			value: [],
			options: [],
		},
	},
];

const jobsByLocationId = jobs => jobs.reduce(
	(byId, job) => {
		const locationId = job.location._id;
		if (locationId in byId) {
			return {
				...byId,
				[ locationId ]: [
					...byId[ locationId ],
					job._id,
				],
			};
		}
		return {
			...byId,
			[ locationId ]: [ job._id ],
		};
	},
	{}
);

const collectLocationsFromJobs = jobs => jobs.reduce(
	(locs, job) => ({
		...locs,
		[ job.location._id ]: job.location,
	}),
	{}
);

const makeMarker = (map, location, item, i) => {
	return new google.maps.Marker({
		map,
		position: location._geo,
		icon: {
			path: 'M22-48h-44v43h16l6 5 6-5h16z',
			fillColor: item.checked ? '#fff' : '#ddd',
			fillOpacity: 1,
			labelOrigin: new google.maps.Point(1.25, -25),
			scale: .45,
		},
		label: { 
			text: `${ i + 1 }`,
			fontSize: '1em',
			fontWeight: 'bold',
			color: '#444',
			zIndex: i,
		},
		title: '',
	});
};

let colTimeout;
let sortTimeout;

const Main = connect(
	(state, props) => ({
		loggedInUser: usersSelectors.getLoggedInUser(state, props),
		pageUrl: appSelectors.getPageUrl(state, props),
		isPageLoaded: appSelectors.isPageLoaded(state, props),
		jobs: jobsTableSelectors.getJobs(state, props),
		users: usersSelectors.getAllUsers(state, props),
		isUsersLoaded: appSelectors.isUsersLoaded(state, props),
	}),
	{
		getJobs: ordersOperations.getJobs,
		setPageLoaded: appOperations.setPageLoaded,
		getUsers: usersOperations.getUsers,
		setModalOpen: appOperations.setModalOpen,
		postSettings: usersOperations.postSettings,
		setUsersLoaded: appOperations.setUsersLoaded,
	}
)(
	({
		loggedInUser,
		pageUrl,
		isPageLoaded,
		getJobs,
		setPageLoaded,
		getUsers,
		users,
		setModalOpen,
		postSettings,
		isUsersLoaded,
		setUsersLoaded,
	}) => {

		if (!window.google || !loggedInUser) {
			return <div />;
		}

		const [ loadingData, setLoadingData ] = useState(false);

		useEffect(
			() => {
				if (!isUsersLoaded) {
					getUsers({}).then(r => setUsersLoaded());
				}
			},
			[]
		);
		const isFirstRender = useIsFirstRender();

		let _tableColumns = tableColumns;
		const tableUI = loggedInUser.settings.ui?.jobsTable;

		let defaultActiveFilters = {};
		let defaultPageSize = 25;
		let defaultPage = 1;

		if (tableUI) {

			if (tableUI.hiddenColumns) {
				_tableColumns = _tableColumns.map(col => {
					if (tableUI.hiddenColumns.includes(col.key)) {
						return {
							...col,
							visible: false,
						};
					}
					return col;
				});
			}

			if (tableUI.sort && tableUI.sort.length) {

				const sortMap = Object.fromEntries(tableUI.sort);

				_tableColumns = _tableColumns.map(col => {
					if (col.key in sortMap) {
						return {
							...col,
							sort: sortMap[ col.key ],
						};
					}
					return col;
				});
			}

			if (tableUI.filters && Object.entries(tableUI.filters).length) {

				_tableColumns = _tableColumns.map(col => {
					if (col.key in tableUI.filters) {
						return {
							...col,
							filter: {
								...col.filter,
								value: tableUI.filters[ col.key ],
							},
						};
					}
					return col;
				});

				defaultActiveFilters = tableUI.filters;
			}

			if (tableUI.pageSize) {
				defaultPageSize = tableUI.pageSize;
			}

			if (tableUI.page) {
				defaultPage = tableUI.page;
			}
		}

		let defaultSortMode = SINGLE_SORT;

		if (tableUI && tableUI.sortMode) {
			defaultSortMode = tableUI.sortMode;
		}

		const google = window.google;
		const [ distinct, setDistinct ] = useState({});
		const [ jobs, updateJobs ] = useState([]);
		const [ tab, setTab ] = useState(TABLE_TAB);
		const [ sortMode, setSortMode ] = useState(defaultSortMode);
		const [ filterCtls, setFilterCtls ] = useState(false);

		const [ filters, setFilters ] = useState(
			_tableColumns.map(
				c => ({
					key: c.key,
					...c.filter,
				})
			)
		);

		const [ filterOptions, setFilterOptions ] = useState([]);

		const [ rowItemsChecked, setRowItemsChecked ] = useState([]);
		const [ activeFilters, setActiveFilters ] = useState(defaultActiveFilters);

		let defaultSort = [];
		if (tableUI && tableUI.sort) {
			defaultSort = tableUI.sort;
		}
		const [ sort, setSort ] = useState(defaultSort);

		const [ pageSize, setPageSize ] = useState(defaultPageSize);
		const [ page, setPage ] = useState(defaultPage);

		const [ total, updateTotal ] = useState(0);
		const [ columns, setColumns ] = useState(_tableColumns);

		const totalPages = Math.ceil(total / pageSize);

		const [ lastIndexChecked, setLastIndexChecked ] = useState(-1);

		const checkRow = rowItemsChecked => i => {

			const item = rowItemsChecked[ i ];
			const newCheckValue = !item.checked;

			const _rowItemsChecked = Object.assign(
				[],
				rowItemsChecked,
				{
					[ i ]: {
						...item,
						checked: newCheckValue,
					},
				}
			);

			if (!newCheckValue) {
				setAllChecked(false);
			} else {
				const all = _rowItemsChecked.every(r => r.checked);

				if (all) {
					setAllChecked(true);
				}
			}

			setRowItemsChecked(_rowItemsChecked);
			setLastIndexChecked(i);
		};

		const [ allChecked, setAllChecked ] = useState(false);
		const [ noneChecked, setNoneChecked ] = useState(true);

		const checkAll = (map, jobs, markers, rowItemsChecked) => () => {

			const newCheckValue = allChecked ? false : true;
			const _rowItemsChecked = rowItemsChecked.map(r => ({ ...r, checked: newCheckValue }));

			if (map && markers.length) {

				const locationsById = collectLocationsFromJobs(jobs);
				const locationPairs = Object.entries(locationsById).map(([ i, l ]) => [
					i,
					{
						...l,
						_geo: new google.maps.LatLng(l.geo[ 0 ], l.geo[ 1 ]),
					}
				]);

				const _markers = markers.map((marker, i) => {

					const item = _rowItemsChecked[ i ];
					const [ locationId, location ] = locationPairs[ i ];

					const oldMarker = markers[ i ];
					oldMarker.setMap(null);

					const newMarker = makeMarker(map, location, item, i);

					return newMarker;
				});

				setMarkers(_markers);
			}

			setRowItemsChecked(_rowItemsChecked);
			setAllChecked(newCheckValue);
		};

		const [ map, setMap ] = useState(null);
		const [ markers, setMarkers ] = useState([]);
		const [ refit, setRefit ] = useState(false);

		useEffect(
			() => {
				if (tab === MAP_TAB) {
					if (!map) {

						const el = document.getElementById('map-cont');
						const _map = new google.maps.Map(
							el,
							{
								mapTypeControl: false,
								streetViewControl: true,
							}
						);

						setMap(_map);
					}

					if (refit) {
						const locationsById = collectLocationsFromJobs(jobs);
						const locationPairs = Object.entries(locationsById).map(([ i, l ]) => [
							i,
							{
								...l,
								_geo: new google.maps.LatLng(l.geo[ 0 ], l.geo[ 1 ]),
							}
						]);

						const lngs = locationPairs.map(([ _id, { _geo } ]) => _geo.lng());
						const lats = locationPairs.map(([ _id, { _geo } ]) => _geo.lat());

						map.fitBounds({
							west: Math.min.apply(null, lngs),
							east: Math.max.apply(null, lngs),
							north: Math.min.apply(null, lats),
							south: Math.max.apply(null, lats),
						});

						setRefit(false);
					}
				}
			},
			[ tab, refit ]
		);

		useEffect(
			() => {
				if (
					map &&
					jobs.length &&
					rowItemsChecked.length &&
					markers.length &&
					!updateInProgress
				) {

					const locationsById = collectLocationsFromJobs(jobs);
					const locationPairs = Object.entries(locationsById).map(([ i, l ]) => [
						i,
						{
							...l,
							_geo: new google.maps.LatLng(l.geo[ 0 ], l.geo[ 1 ]),
						}
					]);

					if (lastIndexChecked > -1) {
						const i = lastIndexChecked;

						const item = rowItemsChecked[ i ];
						const [ locationId, location ] = locationPairs[ i ];

						const oldMarker = markers[ i ];
						oldMarker.setMap(null);

						const newMarker = makeMarker(map, location, item, i);

						const _markers = Object.assign(
							[],
							markers,
							{
								[ i ]: newMarker,
							}
						);

						setMarkers(_markers);
						setLastIndexChecked(-1);
					}
				}
			},
			[ map, rowItemsChecked ]
		);

		useEffect(
			() => {
				
				markers.forEach((marker, i) => {
					google.maps.event.clearInstanceListeners(marker);
					marker.addListener('click', () => checkRow(rowItemsChecked)(i));
				});
			},
			[ markers, rowItemsChecked ]
		);

		useEffect(
			() => {
				if (map) {

					const locationsById = collectLocationsFromJobs(jobs);

					const locationPairs = Object.entries(locationsById).map(([ i, l ]) => [
						i,
						{
							...l,
							_geo: new google.maps.LatLng(l.geo[ 0 ], l.geo[ 1 ]),
						}
					]);

					markers.forEach(m => m.setMap(null));

					setMarkers(locationPairs.map(([ locationId, location ], i) => {
						const marker = makeMarker(map, location, rowItemsChecked[ i ], i);
						return marker;
					}));

					setRefit(true);
				}
			},
			[ map, jobs ]
		);

		const [ updateInProgress, setUpdateInProgress ] = useState(false);
		const [ distinctLoaded, setDistinctLoaded ] = useState(false);

		useEffect(
			() => {

				if (isFirstRender) {
					return;
				}
				setLoadingData(true);

				const calcAttempts = columns.reduce(
					(inc, col) => {
						if (!inc) {
							if (
								[
									'mornings',
									'days',
									'evenings',
									'weekends',
									'all'
								].includes(col.key) &&
								col.visible
							) {
								return true;
							}
						}
						return inc;
					},
					false,
				);

				getJobs({
					page,
					pageSize,
					sort,
					filters: activeFilters,
					distinct: !distinctLoaded,
					calcAttempts,
				}).then(
					r => {

						setLoadingData(false);

						if (r.success) {

							setUpdateInProgress(true);
							updateJobs(r.data.jobs);
							updateTotal(r.data.total);

							if (r.data.distinct) {
								setDistinct(r.data.distinct);
								setDistinctLoaded(true);
								setFilterOptions(
									addFilterOptions(r.data.distinct, filterOptions)
								);
							}

							const _rowItemsChecked = r.data.jobs.map(
								(j, i) => ({
									index: i,
									jobId: j._id,
									locationId: j.location._id,
									checked: false,
								})
							);

							if (map) {

								const locationsById = collectLocationsFromJobs(r.data.jobs);

								const locationPairs = Object.entries(locationsById).map(([ i, l ]) => [
									i,
									{
										...l,
										_geo: new google.maps.LatLng(l.geo[ 0 ], l.geo[ 1 ]),
									}
								]);

								markers.forEach(m => m.setMap(null));

								setMarkers(locationPairs.map(([ locationId, location ], i) => {
									const marker = makeMarker(map, location, _rowItemsChecked[ i ], i);
									return marker;
								}));

								setRefit(true);
							}

							setRowItemsChecked(_rowItemsChecked);
							setAllChecked(false);
							setUpdateInProgress(false);
						}
					},
					e => setLoadingData(false)
				);
			},
			[ page, pageSize, sort, columns, activeFilters ]
		);

		useEffect(
			() => {
				const _filters = filters.reduce(
					(fs, f) => {

						if (f.type === 'text' && f.value) {
							return { ...fs, [ f.key ]: f.value };
						} else if (f.type === 'boolean' && [ '0', '1' ].includes(f.value)) {
							return { ...fs, [ f.key ]: f.value };
						} else if (f.type === 'list' && f.value.length) {
							return { ...fs, [ f.key ]: f.value };
						} else if (f.type === 'date' && (f.value[ 0 ] || f.value[ 1 ])) {
							return { ...fs, [ f.key ]: f.value };
						}

						const { [ f.key ]: omit, ...rest } = fs;

						return rest;
					},
					{}
				);

				setActiveFilters(_filters);

				let userSettings = getUserSettings(loggedInUser);

				userSettings = update(userSettings, {
					ui: {
						jobsTable: {
							filters: {
								$set: _filters,
							},
						},
					},
				});

				if (!isFirstRender) {
					postSettings(loggedInUser.username, userSettings, true);
				}
			},
			[ filters ]
		);

		const saveColSetting = cols => {

			if (colTimeout) {
				clearTimeout(colTimeout);
			}

			colTimeout = setTimeout(
				() => {

					let userSettings = getUserSettings(loggedInUser);

					userSettings = update(userSettings, {
						ui: {
							jobsTable: {
								hiddenColumns: {
									$set: cols.reduce(
										(hidden, col) => col.visible ?
											hidden :
											[ ...hidden, col.key ],
										[]
									),
								},
							},
						},
					});

					postSettings(loggedInUser.username, userSettings, true);
				},
				1000
			);
		};

		const saveSortMode = mode => {

			let userSettings = getUserSettings(loggedInUser);

			userSettings = update(userSettings, {
				ui: {
					jobsTable: {
						sortMode: {
							$set: mode,
						},
					},
				},
			});

			sortTimeout = setTimeout(
				() => {
					postSettings(loggedInUser.username, userSettings, true);
				},
				1000
			);
		};

		useEffect(
			() => {
				if (!isFirstRender) {

					if (sortTimeout) {
						clearTimeout(sortTimeout);
					}

					let userSettings = getUserSettings(loggedInUser);

					userSettings = update(userSettings, {
						ui: {
							jobsTable: jobsTable => update(jobsTable, {
								sortMode: {
									$set: sortMode,
								},
								sort: {
									$set: sort,
								},
							}),
						},
					});

					postSettings(loggedInUser.username, userSettings, true);
				}
			},
			[ sort ]
		);

		const savePageSize = size => {

			let userSettings = getUserSettings(loggedInUser);

			userSettings = update(userSettings, {
				ui: {
					jobsTable: {
						pageSize: {
							$set: size,
						},
					},
				},
			});

			postSettings(loggedInUser.username, userSettings, true);
		};

		const savePage = page => {

			let userSettings = getUserSettings(loggedInUser);

			userSettings = update(userSettings, {
				ui: {
					jobsTable: {
						page: {
							$set: page,
						},
					},
				},
			});

			postSettings(loggedInUser.username, userSettings, true);
		};

		if (!distinct || !Object.entries(distinct).length) {
			return <div />;
		}

		return (
			<div className={ s.root }>
				{ tab === MAP_TAB && <div className={ sx('absTabsCont') }>
					<div className={ s.absTabs }>
						<div className={ s.tabBtns }>
							<div
								className={ sx('tabBtn', 'tableTabBtn', { active: tab === TABLE_TAB }) }
								onClick={ () => setTab(TABLE_TAB) }
							>
								Table
							</div>
							<div
								className={ sx('tabBtn', 'mapTabBtn', { active: tab === MAP_TAB }) }
								onClick={ () => setTab(MAP_TAB) }
							>
								Map
							</div>
						</div>
					</div>
				</div> }
				<div className={ sx('mapTab', { invisible: tab === TABLE_TAB }) }>
					<div id='map-cont' style={{ width: '100%', height: '100%' }} />
				</div>
				<div className={ sx('tableTab', { invisible: tab === MAP_TAB }) }>
					<div className={ s.headerT1 }>
						<div className={ s.left }>
							<div className={ s.assignCont }>
								<Assign
									users={ users }
									onChange={
										user => {

											const items = rowItemsChecked.reduce(
												(items, r) => {
													if (r.checked) {
														return [
															...items,
															[ r.jobId, r.locationId ],
														];
													}
													return items;
												},
												[]
											);

											setModalOpen(
												'assignUser',
												{
													user,
													items,
												}
											);
										}
									}
								/>
							</div>
							<div className={ s.columnsCont }>
								<ColumnSelect
									data={ columns }
									ctx={{
										columns,
										setColumns: cols => {
											setColumns(cols);
										}
									}}
									onChange={
										(col, i) => {

											const currVal = col.visible;

											const _columns = Object.assign(
												[],
												columns,
												{
													[ i ]: {
														...col,
														visible: !currVal,
													},
												}
											);

											setColumns(_columns);
											saveColSetting(_columns);
										}
									}
									optionEl={ ColumnOpt }
								/>
							</div>
							<div className={ s.sortingCont }>
								<Sort
									sortMode={ sortMode }
									sortModes={ sortModes }
									setSortMode={ (mode) => {
										setSortMode(mode);
										saveSortMode(mode);
									}}
									sort={ sort }
									setSort={ setSort }
									columns={ columns }
									setColumns={ setColumns }
								/>
							</div>
							<div
								className={ s.filterBtnCont }
								onClick={ () => setFilterCtls(!filterCtls) }
							>
								<FontAwesomeIcon icon='filter' />
							</div>
						</div>
						<div className={ s.center }>
							<div className={ s.tabBtns }>
								<div
									className={ sx('tabBtn', 'tableTabBtn', { active: tab === TABLE_TAB }) }
									onClick={ () => setTab(TABLE_TAB) }
								>
									Table
								</div>
								<div
									className={ sx('tabBtn', 'mapTabBtn', { active: tab === MAP_TAB }) }
									onClick={ () => setTab(MAP_TAB) }
								>
									Map
								</div>
							</div>
						</div>
						<div className={ s.right }>
							<Pagination
								config={{
									page,
									pageSize,
									total,
									totalPages,
									adjacent: 1,
									boundWidth: 3,
									boundReach: 5,
									separator: '...',
									onPageSizeChange: v => {
										setPageSize(v.value)
										savePageSize(v.value);
									},
									prevPage: () => {
										if (page > 1) {
											setPage(page - 1);
											savePage(page - 1);
										}
									},
									nextPage: () => {
										if (page < totalPages) {
											setPage(page + 1);
											savePage(page + 1);
										}
									},
									onPageChange: n => {
										if (page !== n) {
											setPage(n);
											savePage(n);
										}
									}
								}}
							/>
						</div>
					</div>
					<div className={ s.tableCont }>
						{ !updateInProgress && <Table
							loadingData={ loadingData }
							jobs={ jobs }
							columns={ columns }
							setColumns={ setColumns }
							sort={ sort }
							setSort={ setSort }
							sortMode={ sortMode }
							distinct={ distinct }
							filterCtls={ filterCtls }
							filters={ filters }
							filterOptions={ filterOptions }
							setFilters={ setFilters }
							rowItemsChecked={ rowItemsChecked }
							checkRow={ checkRow(rowItemsChecked) }
							allChecked={ allChecked }
							checkAll={ checkAll(map, jobs, markers, rowItemsChecked) }
						/> }
					</div>
				</div>
			</div>
		);
	}
);

export const components = {
	overlays: [
	],
	main: Main,
};

export default Main;
